import { Component, OnInit, Renderer2 } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ValidationService } from '../kuba/shared/services/validation.service';

import { AuthenticationService } from './../_services/authentication.service';

import { ResetCredential } from './../kuba/users/models/user';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { isAgilPlatform, isDinHmsPlatform } from '../shared/common/common-functions.util';

@Component({
  selector: 'reset-password',
  templateUrl: 'reset-password.component.html',
  styleUrls: ['../login/login-aura.css', 'forget-password.component.css', 'reset-password.component.css', '../login/mdco-auth.scss'],
  standalone: false
})
export class ResetPasswordComponent implements OnInit {
  
  updatePasswordForm: FormGroup;

  userCredentials = new ResetCredential();
  returnValue = 0;
  validationErrors: string[] = [];
  token: string;
  decodedToken: any;
  userId: number;
  createdOn: any;
  regenaratePassword = false;
  currentTime: Date;
  private subscriptions: Subscription[] = [];
  constructor(
    public formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private renderer: Renderer2,
    public translate: TranslateService
  ) {}
  ngOnInit() {
    this.updatePasswordForm = this.formBuilder.group({
      passwordValidation: this.formBuilder.group({
        Password: ['', [Validators.required, Validators.nullValidator]],
        ConfirmPassword: ['', [Validators.required, Validators.nullValidator]]
      })
    });

    this.renderer.addClass(document.body.parentElement, 'loginView');
    this.renderer.addClass(document.body, 'loginView');

    this.token = this.route.snapshot.queryParamMap.get('TokenKey');
    if (!this.token) {
      this.regenaratePassword = true;
      return;
    }
    const resetCredentials = new ResetCredential();
    resetCredentials.Token = this.token;
    this.subscriptions.push(
      this.authenticationService.getUserIdByToken(resetCredentials).subscribe({
        next: (result: any) => {
          this.regenaratePassword = result?.Status !== true || !result?.UserId;
          this.userId = this.regenaratePassword ? undefined : result.UserId;
        },
        error: () => {
          this.regenaratePassword = true;
        }
      })
    );
  }
  get isMedi3(): boolean {
    return window.location.pathname.includes('medi3');
  }

  /** Agil Helse's own reset page, the target of an Agil reset mail's link. */
  get isAgil(): boolean {
    return isAgilPlatform();
  }

  get isDinHms(): boolean {
    return isDinHmsPlatform();
  }

  /** The hero's standing line names the product, so each brand has its own. */
  get storyIntroKey(): string {
    if (this.isMedi3) return 'PASSWORD_RECOVERY.MEDI3_STORY_INTRO';
    if (this.isAgil) return 'PASSWORD_RECOVERY.AGIL_STORY_INTRO';
    return 'PASSWORD_RECOVERY.STORY_INTRO';
  }

  setLanguage(lang: 'en' | 'no'): void {
    this.translate.use(lang);
    localStorage.setItem('languageId', lang === 'en' ? '1' : '2');
    localStorage.setItem('languageMode', lang === 'en' ? 'en-GB' : 'no-NO');
    document.documentElement.lang = lang;
  }

  changePassword() {
    if (!this.userId || this.regenaratePassword || this.returnValue === -1 || this.updatePasswordForm.invalid) return;
    const passwords = this.updatePasswordForm.value.passwordValidation;
    if (passwords.Password !== passwords.ConfirmPassword) return;
    this.returnValue = -1;
    this.userCredentials.Id = this.userId;
    this.userCredentials.Password =
      this.updatePasswordForm.value.passwordValidation.Password;
    this.userCredentials.Token = this.token;
    this.subscriptions.push(
      this.authenticationService.resetPassword(this.userCredentials).subscribe(
        // Success handler
        (result: any) => {
          this.returnValue = result;
          this.userId = undefined;
          this.updatePasswordForm.reset();
        },
        // Error handler
        (errorResponse: any) => {
          // Check if the received error is a function, and if so, execute it
          const actualError =
            typeof errorResponse === 'function'
              ? errorResponse()
              : errorResponse;

          switch (actualError.status) {
            case 400:
            case 401:
              this.regenaratePassword = true;
              this.returnValue = 3;
              break;
            case 409:
              this.returnValue = 2; // Password matches old password (translated via PASSWORD_EXIST)
              break;
            case 422:
              this.validationErrors = actualError.error?.FailedChecks || [];
              this.returnValue = 4;
              break;
            default:
              this.returnValue = 3; // 500 catch all
          }
        }
      )
    );
  }

  onPasswordChange(pwd: string) {
    this.updatePasswordForm.patchValue({
      passwordValidation: {
        Password: pwd,
        ConfirmPassword: pwd
      }
    });
  }

  /**
   * With any changes in validation by child component
   * @param valid boolen
   */
  onValidChange(valid: boolean) {
    const pwGroup = this.updatePasswordForm.get(
      'passwordValidation'
    ) as FormGroup;

    if (valid) {
      pwGroup.setErrors(null);
    } else {
      pwGroup.setErrors({ invalidPassword: true });
    }
  }

  clear() {
    this.userCredentials.Id = 0;
    setTimeout(x => {
      this.returnValue = 0;
    }, 3000);
    this.updatePasswordForm = this.formBuilder.group({
      passwordValidation: this.formBuilder.group({
        Password: '',
        ConfirmPassword: ''
      })
    });
  }
  ngOnDestroy() {
    this.renderer.removeClass(document.body.parentElement, 'loginView');
    this.renderer.removeClass(document.body, 'loginView');

    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
}
